import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import WhatsAppSticky from "@/components/landing/WhatsAppSticky";
import LeadDialog from "@/components/landing/LeadDialog";
import Reveal from "@/components/landing/Reveal";
import ProductCard from "@/components/products/ProductCard";
import { fetchProducts, fetchSiteContent } from "@/lib/api";
import { setPageMeta } from "@/lib/pageMeta";

const assetUrl = (u, w) => {
  if (!u) return "";
  const full = u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
  if (!w || !/\/api\/files\//.test(full)) return full;
  return `${full}${full.includes("?") ? "&" : "?"}w=${w}`;
};

// Non-overlapping budget buckets driven by the product's `price` field.
// New products / edited prices automatically land in the correct tab.
const BUDGET_TABS = [
  { key: "u1000", label: "Diwali Gifts Under ₹1000", min: 0,    max: 1000 },
  { key: "u1500", label: "Diwali Gifts Under ₹1500", min: 1001, max: 1500 },
  { key: "u2000", label: "Diwali Gifts Under ₹2000", min: 1501, max: 2000 },
  { key: "u3000", label: "Diwali Gifts Under ₹3000", min: 2001, max: 3000 },
  { key: "u4500", label: "Diwali Gifts Under ₹4500", min: 3001, max: 4500 },
];

export default function DiwaliCollectionPage() {
  const [dialog, setDialog] = useState({ open: false, type: "meeting" });
  const openMeeting = useCallback(() => setDialog({ open: true, type: "meeting" }), []);
  const openBulk = useCallback(() => setDialog({ open: true, type: "bulk" }), []);
  const closeDialog = useCallback(() => setDialog((d) => ({ ...d, open: false })), []);
  const handlers = useMemo(() => ({ openMeeting, openBulk }), [openMeeting, openBulk]);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteContent, setSiteContent] = useState({});
  useEffect(() => {
    let alive = true;
    fetchProducts({ collection: "diwali-collection" })
      .then((data) => { if (alive) { setProducts(data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    fetchSiteContent().then((c) => { if (alive) setSiteContent(c || {}); }).catch(() => {});
    setPageMeta({
      title: "Diwali Corporate Gift Hampers 2026 — Utsah",
      description: "Curated corporate Diwali gift hampers designed for teams, clients and partners. Free customisation. Pan-India delivery.",
      canonicalPath: "/diwali-collection",
    });
    return () => { alive = false; };
  }, []);

  // Persist selected budget tab across product-detail round trips using
  // sessionStorage. Fresh sessions (new tab / browser) fall back to the
  // first tab. See requirement #3 in conversion-optimisation brief.
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return BUDGET_TABS[0].key;
    try {
      const stored = window.sessionStorage.getItem("dc_active_tab");
      if (stored && BUDGET_TABS.some((t) => t.key === stored)) return stored;
    } catch {}
    return BUDGET_TABS[0].key;
  });
  useEffect(() => {
    try { window.sessionStorage.setItem("dc_active_tab", activeTab); } catch {}
  }, [activeTab]);

  const filtered = useMemo(() => {
    const tab = BUDGET_TABS.find((t) => t.key === activeTab) || BUDGET_TABS[0];
    return products.filter((p) => {
      const price = Number(p.price) || 0;
      return price >= tab.min && price <= tab.max;
    });
  }, [products, activeTab]);

  return (
    <div className="utsah-app">
      <Nav onBookMeeting={openMeeting} />

      {/* HERO */}
      <section className="section dc-hero" data-testid="dc-hero-section" style={{ paddingTop: 40 }}>
        <HeroSlider siteContent={siteContent} onCta={openMeeting} />

        <div className="os-hero-intro dc-hero-intro">
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Festive Catalogue</Reveal>
          <Reveal as="h1" delay={1} className="section-headline dc-headline" style={{ textAlign: "center" }}>
            Celebrate <em>Diwali</em> with Thoughtful Corporate Gifts.
          </Reveal>
        </div>
      </section>

      {/* BUDGET TABS */}
      <section className="dc-toolbar-section" data-testid="dc-budget-tabs-section">
        <div className="section-inner dc-budget-tabs" role="tablist" aria-label="Filter by budget">
          {BUDGET_TABS.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`dc-budget-tab ${active ? "is-active" : ""}`.trim()}
                onClick={() => setActiveTab(t.key)}
                data-testid={`dc-budget-tab-${t.key}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="section dc-grid-section" data-testid="dc-grid-section">
        <div className="section-inner">
          <div className="dc-result-meta">
            <span data-testid="dc-result-count">{filtered.length} {filtered.length === 1 ? "hamper" : "hampers"}</span>
          </div>
          {loading ? (
            <div className="dc-empty" data-testid="dc-loading-state">Loading hampers…</div>
          ) : filtered.length === 0 ? (
            <div className="dc-empty" data-testid="dc-empty-state">
              No hampers in this price range yet.
            </div>
          ) : (
            <div className="dc-grid" data-testid="dc-product-grid">
              {filtered.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer handlers={handlers} />
      <WhatsAppSticky />
      <LeadDialog open={dialog.open} type={dialog.type} source="Diwali Collection" onOpenChange={(v) => (v ? null : closeDialog())} />
      <Toaster position="bottom-center" richColors theme="light" toastOptions={{ style: { fontFamily: "Manrope, system-ui, sans-serif", fontWeight: 500 } }} />
    </div>
  );
}

/* Hero slider — 2 slides, auto-play, dots, swipe support. Images (desktop + mobile) are CMS-driven via Site Content. Falls back to placeholder art when unset. */
const HERO_SLIDES = [
  { label: "Festive Collection — Diwali 2026", tone: "primary", desktopKey: "diwali_hero_1", mobileKey: "diwali_hero_1_mobile_image" },
  { label: "Curated Corporate Hampers", tone: "warm", desktopKey: "diwali_hero_2", mobileKey: "diwali_hero_2_mobile_image" },
];
function HeroSlider({ siteContent, onCta }) {
  const [i, setI] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % HERO_SLIDES.length), 4500);
    return () => clearInterval(t);
  }, []);

  const goTo = (n) => setI(((n % HERO_SLIDES.length) + HERO_SLIDES.length) % HERO_SLIDES.length);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) goTo(i + (dx < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <Reveal
      className="os-hero-media dc-hero-media dc-hero-slider"
      data-testid="dc-hero-slider"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {HERO_SLIDES.map((s, idx) => {
        const desktop = siteContent?.[s.desktopKey] || "";
        const mobile = siteContent?.[s.mobileKey] || "";
        const hasImage = Boolean(desktop || mobile);
        return (
          <div
            key={s.label}
            className={`dc-hero-slide ${i === idx ? "active" : ""}`.trim()}
            data-tone={s.tone}
            aria-hidden={i !== idx}
          >
            {hasImage ? (
              <picture className="dc-hero-picture">
                {mobile ? <source media="(max-width: 720px)" srcSet={assetUrl(mobile, 900)} /> : null}
                <img
                  src={assetUrl(desktop || mobile, 1920)}
                  alt={s.label}
                  fetchpriority={i === 0 ? "high" : "auto"}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                />
              </picture>
            ) : (
              <>
                <svg width="56" height="56" viewBox="0 0 64 64" fill="none" aria-hidden>
                  <rect x="6" y="12" width="52" height="40" rx="3" fill="var(--u-orange)" opacity="0.22" />
                  <circle cx="22" cy="28" r="6" fill="var(--u-orange)" opacity="0.28" />
                  <path d="M52 44L40 30L28 44" stroke="var(--u-orange)" strokeWidth="2" opacity="0.32" />
                </svg>
                <div className="add-image-label">{s.label}</div>
              </>
            )}
          </div>
        );
      })}
      <div className="dc-hero-dots" role="tablist" aria-label="Hero slide controls">
        {HERO_SLIDES.map((_, idx) => (
          <button
            key={idx}
            type="button"
            className={`dc-hero-dot ${i === idx ? "active" : ""}`.trim()}
            aria-label={`Go to slide ${idx + 1}`}
            aria-selected={i === idx}
            onClick={() => goTo(idx)}
            data-testid={`dc-hero-dot-${idx}`}
          />
        ))}
      </div>

      {onCta ? (
        <button
          type="button"
          className="dc-hero-cta"
          onClick={onCta}
          data-testid="dc-hero-cta"
        >
          Speak to a Gift Advisor
          <svg width="16" height="12" viewBox="0 0 18 12" fill="none" aria-hidden>
            <path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : null}
    </Reveal>
  );
}

